import * as React from 'react';
import { OrganType } from '@nkr/enums/organ-type';
import { IDashDetail, IDashListSummary, IDashSearch, DashRegistrationApi } from '@nkr/models/dash';
import { Sidebar } from 'ClientApp/components/layout/sidebar';
import { useForm, UseFormReturn, UseSearchReturn } from 'ClientApp/utils/rest-api';
import * as moment from 'moment';
import { FormControl, Button, Row, Col, FormGroup, ControlLabel, ToggleButtonGroup, ToggleButton, Well, Alert, Label } from 'react-bootstrap';
import { TypeParser } from '@nkr/utils/form';


interface IProps {
    search: UseSearchReturn<IDashSearch, IDashListSummary>
}

export const SearchIntendedRecipientSidebar = (props: IProps) => {
    const search = props.search;
    const data = props.search.selectedRow as IDashListSummary;

    if (!data) return null;

    const isOpen = typeof search.params.selectedRowId === 'number' 
        ? search.params.selectedRowId > 0 
        : parseInt(String(search.params.selectedRowId || '0')) > 0;

    return (
        <Sidebar title='DASH Registration' isOpen={isOpen} onClose={search.unselectRow}>
            <SidebarBody search={search} />
        </Sidebar>
    );
}

const SidebarBody = React.memo(({ search }: IProps) => {
    const data = search.selectedRow as IDashListSummary;
    const form = useForm<IDashDetail>(DashRegistrationApi, data.id || null);
    const recipDob = TypeParser.getDateByFormatOrDefault(form.data.recipDob, 'MM/DD/YYYY', null);
    const recipDobString = TypeParser.formatDate(recipDob, 'YYYY-MM-DD');
    const isReadOnly = data.isReadOnly === true || form.data?.isReadOnly === true;

    const getCenterId = () => {
        if (typeof window !== 'undefined') {
            const pathMatch = window.location.pathname.match(/\/center\/(\d+)\//);
            if (pathMatch) {
                return parseInt(pathMatch[1]);
            }
        }
        if (!data.isRemoteDonor && data.registrationCenterId) {
            return data.registrationCenterId;
        }
        return null;
    };

    const centerId = getCenterId();

    form.setOnSaveComplete((payload) => {
        search.refresh();
    });
    return (
        <>

            {isReadOnly && (
                <Alert bsStyle="warning">
                    This donor is visible through your UDASH domain, but is not yet assigned to your center. Contact and progress information are view-only until the donor is referred to your center.
                </Alert>
            )}

            {data.intendedRecipientIsLocal && (
                <>
                    <Well bsSize="small">
                        <h4>Local Intended Recipient</h4>

                        {!form.isLoaded && "Loading..."}

                        {form.isLoaded && form.data && (
                            <form onSubmit={form.handleSubmit}>
                                <Row style={{ marginBottom: 10 }}>
                                    <Col xs={6}><strong>Intended Recipient First Name:</strong></Col>
                                    <Col xs={6}><FormControl name='recipFirstName' type="text" value={form.data.recipFirstName ?? ''} onChange={form.handleStringChange} disabled={isReadOnly} /></Col>
                                </Row>
                                <Row style={{ marginBottom: 10 }}>
                                    <Col xs={6}><strong>Intended Recipient Last Name:</strong></Col>
                                    <Col xs={6}><FormControl name='recipLastName' type="text" value={form.data.recipLastName ?? ''} onChange={form.handleStringChange} disabled={isReadOnly} /></Col>
                                </Row>
                                <Row style={{ marginBottom: 10 }}>
                                    <Col xs={6}><strong>Intended Recipient DOB:</strong></Col>
                                    <Col xs={6}><FormControl name='recipDob' type="date" value={recipDobString || ''} onChange={form.getDateHandler("MM/DD/YYYY")} disabled={isReadOnly} /></Col>
                                </Row>
                                <Row>
                                    <Col xs={6}>&nbsp;</Col>
                                    <Col xs={6}><Button bsStyle="primary" type='submit' disabled={isReadOnly}>Update</Button></Col>
                                </Row>
                            </form>
                        )}

                    </Well>
                </>
            )}

            {!data.intendedRecipientIsLocal && (
                <Well bsSize="small">
                    <h4>Remote Intended Recipient</h4>
                    <Row>
                        <Col xs={6}><strong>Intended Recipient Center:</strong></Col>
                        <Col xs={6}><Label bsStyle="warning">{data.intendedRecipientCenterAlias}</Label></Col>
                    </Row>
                    <Row>
                        <Col xs={6}><strong>Intended Recipient First Name:</strong></Col>
                        <Col xs={6}>{data.intendedRecipientFirstName}</Col>
                    </Row>
                    <Row>
                        <Col xs={6}><strong>Intended Recipient Last Name:</strong></Col>
                        <Col xs={6}>{data.intendedRecipientLastName}</Col>
                    </Row>
                    <Row>
                        <Col xs={6}><strong>Intended Recipient DOB:</strong></Col>
                        <Col xs={6}>{data.intendedRecipientDob}</Col>
                    </Row>
                </Well>
            )}

            {data.isRemoteDonor && (
                <Alert bsStyle="success">
                    <h4>Network Status Details (from Remote Center)</h4>
                    <Row>
                        <Col xs={6}><strong>Registration Center:</strong></Col>
                        <Col xs={6}><Label bsStyle="warning">{data.registrationCenterAlias}</Label></Col>
                    </Row>
                    <Row>
                        <Col xs={6}><strong>Last Updated:</strong></Col>
                        <Col xs={6}>{data.networkStatusLastUpdatedDate}</Col>
                    </Row>
                    <Row>
                        <Col xs={6}><strong>Center Workflow Status:</strong></Col>
                        <Col xs={6}>{data.centerWorkflowDescription}</Col>
                    </Row>
                    <Row>
                        <Col xs={6}><strong>Network Status:</strong></Col>
                        <Col xs={6}>{data.networkStatusDescription}</Col>
                    </Row>
                    <Row>
                        <Col xs={6}><strong>{data.networkStatusDescription} Date:</strong></Col>
                        <Col xs={6}>{data.networkStatusDate}</Col>
                    </Row>
                    <Row>
                        <Col xs={6}><strong>Network Status Comments:</strong></Col>
                        <Col xs={6}>{data.networkStatusComments}</Col>
                    </Row>
                </Alert>
            )}


            <Alert bsStyle="warning">
                <h4>{data.isRemoteDonor ? 'Remote' : 'Local'} Donor Candidate</h4>
                <Row>
                    <Col xs={6}><strong>Relationship to Intended Recipient:</strong></Col>
                    <Col xs={6}><Label bsStyle="info">{data.intendedRecipientRelationship}</Label></Col>
                </Row>
                <Row>
                    <Col xs={6}><strong>First Name:</strong></Col>
                    <Col xs={6}>{data.firstName}</Col>
                </Row>
                <Row>
                    <Col xs={6}><strong>Last Name:</strong></Col>
                    <Col xs={6}>{data.lastName}</Col>
                </Row>
                <Row>
                    <Col xs={6}><strong>DOB:</strong></Col>
                    <Col xs={6}>{data.dob}</Col>
                </Row>
                <Row>
                    <Col xs={6}><strong>ABO:</strong></Col>
                    <Col xs={6}>{data.abo}</Col>
                </Row>
                <Row>
                    <Col xs={6}><strong>Location:</strong></Col>
                    <Col xs={6}>{data.location}</Col>
                </Row>
                {!data.isRemoteDonor && centerId && (
                    <Row style={{ marginTop: 10 }}>
                        <Col xs={12}>
                            <Button 
                                bsStyle="primary" 
                                href={`/center/${centerId}/donors/donor-intake/details/${data.id}`}
                            >
                                View Details
                            </Button>
                        </Col>
                    </Row>
                )}
            </Alert>
            <Alert bsStyle="warning">
                <h4>Additional Registration Details</h4>
                <Row>
                    <Col xs={6}><strong>Registration Step:</strong></Col>
                    <Col xs={6}>{data.registrationStatus}</Col>
                </Row>
                <Row>
                    <Col xs={6}><strong>Registered:</strong></Col>
                    <Col xs={6}>{data.registrationDate}</Col>
                </Row>
                <Row>
                    <Col xs={6}><strong>Referred:</strong></Col>
                    <Col xs={6}>{data.intakeCompleteDate}</Col>
                </Row>
                <Row>
                    <Col xs={6}><strong>Donated:</strong></Col>
                    <Col xs={6}>{data.donationDate}</Col>
                </Row>
            </Alert>
        </>
    );
}, (prev, next) => prev.search.selectedRow?.id === next.search.selectedRow?.id);
/*


    registrationDate                         : string,
    intakeCompleteDate                       : string | null,
    donationDate                             : string | null,

    donationType                             : string | null,
    donationEpletMm                          : string | null,
    isInExpertSystem                         : boolean | null,

    intendedDonationType                     : string, //SV/FV/DD/NDD
    intendedRecipientIsLocal                 : boolean | null,
    intendedRecipientCenterId                : number | null,
    intendedRecipientCenterAlias             : string | null,
    intendedRecipientCenterIsParticipating   : boolean | null,
    intendedRecipientFirstName               : string | null,
    intendedRecipientLastName                : string | null,
    intendedRecipientDob                     : string | null,
    intendedRecipientRelationship            : string | null,

    coordinatorId                            : number | null,
    coordinatorName                          : string | null,
    coordinatorEmail                         : string | null,

    recipientCoordinatorId                   : number | null,
    recipientCoordinatorName                 : string | null,
    recipientCoordinatorEmail                : string | null,
*/